import * as React from 'react'
import { observer } from 'mobx-react'
import { Table, Select, Tag, Icon, Col } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import {
  IntegrationStore,
  Integrators,
  TIntegrationCategoryName,
  IntegrationCategory,
} from '../IntegrationStore'
import { BlazeButton } from '../../../../components/CustomButtons/BlazeButton'
import { filterOption } from '../../../../utilities/helpers'
import { THEME_COLORS } from '@getgreenline/shared'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props<T extends Integrators> {
  integrationStore: IntegrationStore<T>
  onIntegrationCategorySelect: (
    index: number,
    categoryItem: IntegrationCategory<T>,
    categoryId: string | undefined,
  ) => void
  onUpdate: () => void
  isLoading: boolean
  description?: JSX.Element | undefined
}

interface State {
  updating: boolean
}

interface IIntegrationCategoryTableRecord {
  key: string
  greenlineCategory: JSX.Element
  integrationCategory: JSX.Element
  change: JSX.Element | undefined
}

@observer
export class IntegrationCategories<T extends Integrators> extends React.Component<Props<T>, State> {
  constructor(props: Props<T>) {
    super(props)
    this.state = {
      updating: false,
    }
  }

  get isUpdateButtonDisabled(): boolean {
    const { integrationStore } = this.props
    const { updating } = this.state
    return !integrationStore.anyUpdated || updating || integrationStore.hasNoApiKey
  }

  get columns() {
    const { integrationStore } = this.props
    const { updating } = this.state
    const baseColumns: ColumnProps<IIntegrationCategoryTableRecord>[] = [
      {
        title: `${BLAZE_COMPANY} Category`,
        dataIndex: 'greenlineCategory',
        key: 'greenlineCategory',
        width: 250,
      },
      {
        title: (
          <div>
            <span>{integrationStore.integrationName} Category</span>
            <BlazeButton
              buttonProps={{
                className: 'ml-2',
                loading: updating,
                onClick: this.updateCategories,
                disabled: this.isUpdateButtonDisabled,
              }}
              titleProps={{
                title: 'Update',
              }}
            />
          </div>
        ),
        dataIndex: 'integrationCategory',
        key: 'integrationCategory',
        width: 250,
      },
    ]

    if (integrationStore.anyUpdated) {
      baseColumns.push({
        title: 'Change',
        dataIndex: 'change',
        key: 'change',
        width: 'fit-content',
      })
    }

    return baseColumns
  }

  get dataSource(): IIntegrationCategoryTableRecord[] {
    const { integrationStore, onIntegrationCategorySelect } = this.props
    return integrationStore.integrationCategories.map((categoryItem, index) => {
      const category = categoryItem.greenlineCategory
      return {
        key: category.id,
        greenlineCategory: (
          <div>
            {category.parentCategoryId && <span className='mr-3' />}
            <span
              className='fa fa-square mr-3'
              style={{ color: category.colorHex || THEME_COLORS.MONO.monodd }}
            />
            <span>{category.name}</span>
          </div>
        ),
        integrationCategory: (
          <Select<TIntegrationCategoryName<Integrators> | undefined>
            style={{ width: 200 }}
            dropdownMatchSelectWidth={false}
            value={categoryItem.updatedIntegrationCategory?.name || undefined}
            allowClear
            placeholder='Select a category'
            disabled={categoryItem.isDisabled || integrationStore.hasNoApiKey}
            onChange={(categoryId) => onIntegrationCategorySelect(index, categoryItem, categoryId)}
            filterOptions={filterOption}
            showSearch
          >
            {categoryItem.availableCategories.map((category, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Select.Option key={index} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        ),
        change: categoryItem.isUpdated ? (
          <div className='d-flex align-items-center'>
            <Tag color={categoryItem.greenlineCategory.colorHex}>
              <s>
                {categoryItem.integrationCategory?.name ||
                  `No ${integrationStore.integrationName} category`}
              </s>
            </Tag>
            <Icon type='arrow-right' className='mr-2' />
            <Tag color={categoryItem.greenlineCategory.colorHex}>
              {categoryItem.updatedIntegrationCategory?.name ||
                `No ${integrationStore.integrationName} category`}
            </Tag>
          </div>
        ) : undefined,
      }
    })
  }

  updateCategories = async () => {
    this.setState({ updating: true })

    await this.props.onUpdate()

    this.setState({ updating: false })
  }

  render() {
    const { description, isLoading } = this.props
    return (
      <div>
        {description || null}
        <Table
          className='no-background-table intercom-pb-4'
          rowClassName={() => 'background-white editable-row'}
          columns={this.columns}
          dataSource={this.dataSource}
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={isLoading}
        />
      </div>
    )
  }
}
